import { defineComponent as _defineComponent } from 'vue';
import { createCommentVNode as _createCommentVNode, renderSlot as _renderSlot, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock, Fragment as _Fragment, renderList as _renderList, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle } from "vue";
const _hoisted_1 = { class: "mx-auto px-2 lg:px-8 max-w-7xl" };
const _hoisted_2 = { class: "text-center mb-8" };
const _hoisted_3 = { class: "text-4xl lg:text-5xl font-bold text-black mb-4 dark:text-white" };
const _hoisted_4 = {
    key: 0,
    class: "text-lg text-neutral-600 mx-auto dark:text-neutral-400 max-w-2xl"
};
const _hoisted_5 = {
    key: 0,
    class: "bg-white dark:bg-neutral-950 rounded-2xl shadow-lg p-8 text-center border border-neutral-200 dark:border-neutral-800"
};
const _hoisted_6 = {
    key: 0,
    class: "bg-white dark:bg-neutral-950 rounded-2xl shadow-lg p-12 text-center border border-neutral-200 dark:border-neutral-800"
};
const _hoisted_7 = { class: "bg-white dark:bg-neutral-950 rounded-2xl shadow-lg p-8 text-center border border-neutral-200 dark:border-neutral-800" };
const _hoisted_8 = { class: "text-red-600 dark:text-red-400 mb-4" };
const _hoisted_9 = { class: "hidden lg:grid lg:grid-cols-3 gap-8" };
const _hoisted_10 = { class: "items-center mb-4 flex" };
const _hoisted_11 = { class: "flex gap-1" };
const _hoisted_12 = { class: "text-neutral-700 mb-6 dark:text-neutral-300 whitespace-pre-line line-clamp-6 flex-grow" };
const _hoisted_13 = { class: "items-center flex gap-3 mt-auto" };
const _hoisted_14 = ["src", "alt"];
const _hoisted_15 = { class: "font-bold text-black dark:text-white" };
const _hoisted_16 = ["href"];
const _hoisted_17 = { key: 1 };
const _hoisted_18 = {
    key: 0,
    class: "text-sm text-neutral-600 dark:text-neutral-400"
};
const _hoisted_19 = { class: "lg:hidden relative px-8" };
const _hoisted_20 = { class: "overflow-hidden" };
const _hoisted_21 = { class: "items-center mb-4 flex" };
const _hoisted_22 = { class: "flex gap-1" };
const _hoisted_23 = { class: "text-neutral-700 mb-6 dark:text-neutral-300 whitespace-pre-line line-clamp-5 flex-grow" };
const _hoisted_24 = { class: "items-center flex gap-3 mt-auto" };
const _hoisted_25 = ["src", "alt"];
const _hoisted_26 = { class: "font-bold text-black dark:text-white" };
const _hoisted_27 = ["href"];
const _hoisted_28 = { key: 1 };
const _hoisted_29 = {
    key: 0,
    class: "text-sm text-neutral-600 dark:text-neutral-400"
};
const _hoisted_30 = { class: "flex justify-center gap-2 mt-6" };
const _hoisted_31 = ["onClick", "aria-label"];
const _hoisted_32 = { class: "bg-white dark:bg-neutral-950 rounded-2xl shadow-lg p-8 text-center border border-neutral-200 dark:border-neutral-800" };
const _hoisted_33 = { class: "text-neutral-700 dark:text-neutral-300" };
import { computed, ref, watch, onMounted, onUnmounted } from 'vue';
import { useGoogleReviews } from '../composables/useGoogleReviews';
export default /*@__PURE__*/ _defineComponent({
    __name: 'GoogleReviews',
    props: {
        apiClient: {},
        consentGranted: { type: Boolean },
        heading: { default: 'Google Bewertungen' },
        maxItems: { default: 10 },
        cacheKey: {},
        emptyStateText: { default: 'Noch keine Bewertungen vorhanden.' }
    },
    setup(__props) {
        const props = __props;
        const consent = ref(props.consentGranted);
        const rootEl = ref(null);
        // Carousel State
        const currentSlide = ref(0);
        const carouselTrack = ref(null);
        const autoplayInterval = ref(null);
        const autoplayTimeout = ref(null);
        const touchStartX = ref(0);
        const touchEndX = ref(0);
        watch(() => props.consentGranted, (newValue) => {
            consent.value = newValue;
        });
        const cacheKey = computed(() => props.cacheKey ?? `google-reviews-${props.apiClient.getProjectIdentifier()}`);
        const { reviews, meta, isLoading, error, hasFetched, refetch } = useGoogleReviews({
            apiClient: props.apiClient,
            target: rootEl,
            consentGranted: consent,
            cacheKey: cacheKey.value,
            once: true
        });
        const displayReviews = computed(() => {
            return reviews.value.slice(0, props.maxItems);
        });
        // Auf Mobile konnte die Section sonst vorzeitig auf h-0 kollabieren.
        // Damit Consent-, Loading- und Empty-State zuverlässig sichtbar bleiben,
        // zeigen wir die Section, sobald irgendein relevanter Zustand vorliegt.
        const shouldShowSection = computed(() => {
            if (!consent.value)
                return true;
            if (isLoading.value)
                return true;
            if (error.value)
                return true;
            if (displayReviews.value.length > 0)
                return true;
            if (hasFetched.value)
                return true;
            return true;
        });
        // Carousel Functions
        const nextSlide = () => {
            if (displayReviews.value.length === 0)
                return;
            currentSlide.value = (currentSlide.value + 1) % displayReviews.value.length;
        };
        const prevSlide = () => {
            if (displayReviews.value.length === 0)
                return;
            currentSlide.value = currentSlide.value === 0
                ? displayReviews.value.length - 1
                : currentSlide.value - 1;
        };
        const goToSlide = (index) => {
            currentSlide.value = index;
            resetAutoplay();
        };
        // Touch Handlers
        const handleTouchStart = (e) => {
            touchStartX.value = e.touches[0].clientX;
            stopAutoplay();
        };
        const handleTouchMove = (e) => {
            touchEndX.value = e.touches[0].clientX;
        };
        const handleTouchEnd = () => {
            const swipeThreshold = 50;
            const diff = touchStartX.value - touchEndX.value;
            if (Math.abs(diff) > swipeThreshold) {
                if (diff > 0) {
                    nextSlide();
                }
                else {
                    prevSlide();
                }
            }
            resetAutoplay();
        };
        // Autoplay Functions
        const startAutoplay = () => {
            if (displayReviews.value.length <= 1)
                return;
            autoplayInterval.value = window.setInterval(() => {
                nextSlide();
            }, 5000); // 5 Sekunden
        };
        const stopAutoplay = () => {
            if (autoplayInterval.value) {
                clearInterval(autoplayInterval.value);
                autoplayInterval.value = null;
            }
        };
        const resetAutoplay = () => {
            stopAutoplay();
            // Clear any pending restart
            if (autoplayTimeout.value) {
                clearTimeout(autoplayTimeout.value);
                autoplayTimeout.value = null;
            }
            // Verzögere den Neustart um 10 Sekunden nach manueller Interaktion
            autoplayTimeout.value = window.setTimeout(() => {
                startAutoplay();
            }, 10000); // 10 Sekunden Pause nach manueller Interaktion
        };
        // Lifecycle
        onMounted(() => {
            startAutoplay();
        });
        onUnmounted(() => {
            stopAutoplay();
            if (autoplayTimeout.value) {
                clearTimeout(autoplayTimeout.value);
            }
        });
        // Watch for reviews changes to restart autoplay
        watch(displayReviews, () => {
            resetAutoplay();
        });
        const formatDate = (dateString) => {
            const date = new Date(dateString);
            return date.toLocaleDateString('de-DE', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        };
        // Image Error Handler - Fallback zu Favicon wenn Bild nicht geladen werden kann
        const handleImageError = (event) => {
            const img = event.target;
            if (img.src !== '/favicon.ico') {
                img.src = '/favicon.ico';
            }
        };
        const openCookieSettings = () => {
            if (typeof window === 'undefined')
                return;
            window.dispatchEvent(new CustomEvent('open-cookie-settings'));
        };
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock(_Fragment, null, [
                _createCommentVNode(" Immer rendern für IntersectionObserver, aber visuell verstecken wenn keine Reviews "),
                _createElementVNode("section", {
                    ref_key: "rootEl",
                    ref: rootEl,
                    class: _normalizeClass(["bg-neutral-50 dark:bg-neutral-900 transition-opacity duration-300", [
                            shouldShowSection.value ? 'py-12 lg:py-16 opacity-100' : 'py-0 opacity-0 pointer-events-none h-0 overflow-hidden'
                        ]])
                }, [
                    _createElementVNode("div", _hoisted_1, [
                        _createCommentVNode(" Header "),
                        _createElementVNode("div", _hoisted_2, [
                            _createElementVNode("p", _hoisted_3, [
                                _renderSlot(_ctx.$slots, "title", {}, () => [
                                    _createTextVNode(_toDisplayString(_ctx.heading), 1 /* TEXT */)
                                ])
                            ]),
                            _createCommentVNode(" Subtitle Slot "),
                            _renderSlot(_ctx.$slots, "subtitle", {
                                meta: _unref(meta),
                                isLoading: _unref(isLoading),
                                hasConsent: consent.value
                            }, () => [
                                _createCommentVNode(" Default subtitle with review count "),
                                (_unref(meta)?.totalReviewCount)
                                    ? (_openBlock(), _createElementBlock("p", _hoisted_4, " Über " + _toDisplayString(_unref(meta).totalReviewCount) + " zufriedene Kunden haben uns bereits bewertet ", 1 /* TEXT */))
                                    : _createCommentVNode("v-if", true)
                            ])
                        ]),
                        _createCommentVNode(" Consent Required "),
                        (!consent.value)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                                _renderSlot(_ctx.$slots, "consent", {}, () => [
                                    _cache[1] || (_cache[1] = _createElementVNode("p", { class: "text-neutral-700 dark:text-neutral-300" }, " Bitte stimmen Sie der Kategorie „Externe Inhalte“ zu, um Google Bewertungen zu sehen. ", -1 /* CACHED */)),
                                    _createElementVNode("button", {
                                        type: "button",
                                        class: "mt-4 inline-flex items-center rounded-full bg-black px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-neutral-800 dark:bg-white dark:text-black dark:hover:bg-neutral-200",
                                        onClick: openCookieSettings
                                    }, " Zu den Cookie-Einstellungen ")
                                ])
                            ]))
                            : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                _createCommentVNode(" Reviews Content "),
                                _createElementVNode("div", null, [
                                    _createCommentVNode(" Loading State "),
                                    (_unref(isLoading))
                                        ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                                            _renderSlot(_ctx.$slots, "loading", {}, () => [
                                                _cache[2] || (_cache[2] = _createElementVNode("p", { class: "text-neutral-700 dark:text-neutral-300" }, "Bewertungen werden geladen …", -1 /* CACHED */))
                                            ])
                                        ]))
                                        : (_unref(error))
                                            ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                                _createCommentVNode(" Error State "),
                                                _createElementVNode("div", _hoisted_7, [
                                                    _renderSlot(_ctx.$slots, "error", { error: _unref(error) }, () => [
                                                        _createElementVNode("p", _hoisted_8, _toDisplayString(_unref(error)), 1 /* TEXT */),
                                                        _createElementVNode("button", {
                                                            type: "button",
                                                            onClick: _cache[0] || (_cache[0] =
                                                                //@ts-ignore
                                                                (...args) => (_unref(refetch) && _unref(refetch)(...args))),
                                                            class: "bg-black dark:bg-white text-white dark:text-black px-2 py-2 rounded-full font-semibold hover:bg-neutral-800 dark:hover:bg-neutral-100 transition-colors"
                                                        }, " Erneut versuchen ")
                                                    ])
                                                ])
                                            ], 2112 /* STABLE_FRAGMENT, DEV_ROOT_FRAGMENT */))
                                            : (displayReviews.value.length)
                                                ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                                                    _createCommentVNode(" Reviews Container "),
                                                    _createElementVNode("div", null, [
                                                        _createCommentVNode(" Desktop Grid (large screens only) "),
                                                        _createElementVNode("div", _hoisted_9, [
                                                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(displayReviews.value, (review, index) => {
                                                                return (_openBlock(), _createElementBlock("div", {
                                                                    key: review.reviewId,
                                                                    class: "bg-white dark:bg-neutral-950 rounded-2xl shadow-lg p-9 border border-neutral-200 dark:border-neutral-800 opacity-0 animate-fade-in flex flex-col min-h-[320px]",
                                                                    style: _normalizeStyle({ animationDelay: `${index * 100}ms` })
                                                                }, [
                                                                    _createCommentVNode(" Stars "),
                                                                    _createElementVNode("div", _hoisted_10, [
                                                                        _createElementVNode("div", _hoisted_11, [
                                                                            (_openBlock(), _createElementBlock(_Fragment, null, _renderList(5, (n) => {
                                                                                return _createElementVNode("svg", {
                                                                                    key: n,
                                                                                    class: _normalizeClass(["w-5 h-5 text-black dark:text-white", n <= review.rating ? 'opacity-100' : 'opacity-20']),
                                                                                    fill: "currentColor",
                                                                                    viewBox: "0 0 20 20"
                                                                                }, [...(_cache[3] || (_cache[3] = [
                                                                                        _createElementVNode("path", { d: "M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" }, null, -1 /* CACHED */)
                                                                                    ]))], 2 /* CLASS */);
                                                                            }), 64 /* STABLE_FRAGMENT */))
                                                                        ])
                                                                    ]),
                                                                    _createCommentVNode(" Review Text "),
                                                                    _createElementVNode("p", _hoisted_12, _toDisplayString(review.text || ''), 1 /* TEXT */),
                                                                    _createCommentVNode(" Author Info "),
                                                                    _createElementVNode("div", _hoisted_13, [
                                                                        _createElementVNode("img", {
                                                                            src: review.authorProfilePhoto || '/favicon.ico',
                                                                            alt: review.authorProfilePhoto ? `Profilbild von ${review.authorName}` : 'Restaurant Logo',
                                                                            loading: "lazy",
                                                                            class: "w-12 h-12 bg-neutral-200 dark:bg-neutral-800 rounded-full object-cover",
                                                                            onError: handleImageError
                                                                        }, null, 40 /* PROPS, NEED_HYDRATION */, _hoisted_14),
                                                                        _createElementVNode("div", null, [
                                                                            _createElementVNode("p", _hoisted_15, [
                                                                                (review.authorProfileUrl)
                                                                                    ? (_openBlock(), _createElementBlock("a", {
                                                                                        key: 0,
                                                                                        href: review.authorProfileUrl,
                                                                                        target: "_blank",
                                                                                        rel: "noopener",
                                                                                        class: "hover:underline"
                                                                                    }, _toDisplayString(review.authorName), 9 /* TEXT, PROPS */, _hoisted_16))
                                                                                    : (_openBlock(), _createElementBlock("span", _hoisted_17, _toDisplayString(review.authorName), 1 /* TEXT */))
                                                                            ]),
                                                                            (review.publishedAt)
                                                                                ? (_openBlock(), _createElementBlock("p", _hoisted_18, _toDisplayString(formatDate(review.publishedAt)), 1 /* TEXT */))
                                                                                : _createCommentVNode("v-if", true)
                                                                        ])
                                                                    ])
                                                                ], 4 /* STYLE */));
                                                            }), 128 /* KEYED_FRAGMENT */))
                                                        ]),
                                                        _createCommentVNode(" Mobile & Tablet Carousel "),
                                                        _createElementVNode("div", _hoisted_19, [
                                                            _createElementVNode("div", _hoisted_20, [
                                                                _createElementVNode("div", {
                                                                    ref_key: "carouselTrack",
                                                                    ref: carouselTrack,
                                                                    class: "flex transition-transform duration-500 ease-out",
                                                                    style: _normalizeStyle({ transform: `translateX(-${currentSlide.value * 100}%)` }),
                                                                    onTouchstart: handleTouchStart,
                                                                    onTouchmove: handleTouchMove,
                                                                    onTouchend: handleTouchEnd
                                                                }, [
                                                                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(displayReviews.value, (review, index) => {
                                                                        return (_openBlock(), _createElementBlock("div", {
                                                                            key: review.reviewId,
                                                                            class: "w-full flex-shrink-0 px-4"
                                                                        }, [
                                                                            _createElementVNode("div", {
                                                                                class: "bg-white dark:bg-neutral-950 rounded-2xl shadow-lg p-7 border border-neutral-200 dark:border-neutral-800 opacity-0 animate-fade-in flex flex-col min-h-[280px]",
                                                                                style: _normalizeStyle({ animationDelay: `${index * 100}ms` })
                                                                            }, [
                                                                                _createCommentVNode(" Stars "),
                                                                                _createElementVNode("div", _hoisted_21, [
                                                                                    _createElementVNode("div", _hoisted_22, [
                                                                                        (_openBlock(), _createElementBlock(_Fragment, null, _renderList(5, (n) => {
                                                                                            return _createElementVNode("svg", {
                                                                                                key: n,
                                                                                                class: _normalizeClass(["w-5 h-5 text-black dark:text-white", n <= review.rating ? 'opacity-100' : 'opacity-20']),
                                                                                                fill: "currentColor",
                                                                                                viewBox: "0 0 20 20"
                                                                                            }, [...(_cache[4] || (_cache[4] = [
                                                                                                    _createElementVNode("path", { d: "M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" }, null, -1 /* CACHED */)
                                                                                                ]))], 2 /* CLASS */);
                                                                                        }), 64 /* STABLE_FRAGMENT */))
                                                                                    ])
                                                                                ]),
                                                                                _createCommentVNode(" Review Text "),
                                                                                _createElementVNode("p", _hoisted_23, _toDisplayString(review.text || ''), 1 /* TEXT */),
                                                                                _createCommentVNode(" Author Info "),
                                                                                _createElementVNode("div", _hoisted_24, [
                                                                                    _createElementVNode("img", {
                                                                                        src: review.authorProfilePhoto || '/favicon.ico',
                                                                                        alt: review.authorProfilePhoto ? `Profilbild von ${review.authorName}` : 'Restaurant Logo',
                                                                                        loading: "lazy",
                                                                                        class: "w-12 h-12 bg-neutral-200 dark:bg-neutral-800 rounded-full object-cover",
                                                                                        onError: handleImageError
                                                                                    }, null, 40 /* PROPS, NEED_HYDRATION */, _hoisted_25),
                                                                                    _createElementVNode("div", null, [
                                                                                        _createElementVNode("p", _hoisted_26, [
                                                                                            (review.authorProfileUrl)
                                                                                                ? (_openBlock(), _createElementBlock("a", {
                                                                                                    key: 0,
                                                                                                    href: review.authorProfileUrl,
                                                                                                    target: "_blank",
                                                                                                    rel: "noopener",
                                                                                                    class: "hover:underline"
                                                                                                }, _toDisplayString(review.authorName), 9 /* TEXT, PROPS */, _hoisted_27))
                                                                                                : (_openBlock(), _createElementBlock("span", _hoisted_28, _toDisplayString(review.authorName), 1 /* TEXT */))
                                                                                        ]),
                                                                                        (review.publishedAt)
                                                                                            ? (_openBlock(), _createElementBlock("p", _hoisted_29, _toDisplayString(formatDate(review.publishedAt)), 1 /* TEXT */))
                                                                                            : _createCommentVNode("v-if", true)
                                                                                    ])
                                                                                ])
                                                                            ], 4 /* STYLE */)
                                                                        ]));
                                                                    }), 128 /* KEYED_FRAGMENT */))
                                                                ], 36 /* STYLE, NEED_HYDRATION */)
                                                            ]),
                                                            _createCommentVNode(" Carousel Arrow Buttons (outside overflow-hidden to prevent clipping) "),
                                                            _createElementVNode("button", {
                                                                onClick: prevSlide,
                                                                class: "absolute left-0 top-1/2 -translate-y-1/2 bg-white dark:bg-neutral-900 rounded-full p-2 shadow-lg border border-neutral-200 dark:border-neutral-800 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors z-10",
                                                                "aria-label": "Vorherige Bewertung"
                                                            }, [...(_cache[5] || (_cache[5] = [
                                                                    _createElementVNode("svg", {
                                                                        class: "w-6 h-6 text-black dark:text-white",
                                                                        fill: "none",
                                                                        stroke: "currentColor",
                                                                        viewBox: "0 0 24 24"
                                                                    }, [
                                                                        _createElementVNode("path", {
                                                                            "stroke-linecap": "round",
                                                                            "stroke-linejoin": "round",
                                                                            "stroke-width": "2",
                                                                            d: "M15 19l-7-7 7-7"
                                                                        })
                                                                    ], -1 /* CACHED */)
                                                                ]))]),
                                                            _createElementVNode("button", {
                                                                onClick: nextSlide,
                                                                class: "absolute right-0 top-1/2 -translate-y-1/2 bg-white dark:bg-neutral-900 rounded-full p-2 shadow-lg border border-neutral-200 dark:border-neutral-800 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors z-10",
                                                                "aria-label": "Nächste Bewertung"
                                                            }, [...(_cache[6] || (_cache[6] = [
                                                                    _createElementVNode("svg", {
                                                                        class: "w-6 h-6 text-black dark:text-white",
                                                                        fill: "none",
                                                                        stroke: "currentColor",
                                                                        viewBox: "0 0 24 24"
                                                                    }, [
                                                                        _createElementVNode("path", {
                                                                            "stroke-linecap": "round",
                                                                            "stroke-linejoin": "round",
                                                                            "stroke-width": "2",
                                                                            d: "M9 5l7 7-7 7"
                                                                        })
                                                                    ], -1 /* CACHED */)
                                                                ]))]),
                                                            _createCommentVNode(" Carousel Navigation Dots "),
                                                            _createElementVNode("div", _hoisted_30, [
                                                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(displayReviews.value, (_, index) => {
                                                                    return (_openBlock(), _createElementBlock("button", {
                                                                        key: index,
                                                                        onClick: ($event) => (goToSlide(index)),
                                                                        class: _normalizeClass(["w-2 h-2 rounded-full transition-all duration-300", currentSlide.value === index ? 'bg-black dark:bg-white w-8' : 'bg-neutral-300 dark:bg-neutral-700']),
                                                                        "aria-label": `Gehe zu Bewertung ${index + 1}`
                                                                    }, null, 10 /* CLASS, PROPS */, _hoisted_31));
                                                                }), 128 /* KEYED_FRAGMENT */))
                                                            ])
                                                        ])
                                                    ])
                                                ], 2112 /* STABLE_FRAGMENT, DEV_ROOT_FRAGMENT */))
                                                : (_openBlock(), _createElementBlock(_Fragment, { key: 3 }, [
                                                    _createCommentVNode(" Empty State "),
                                                    _createElementVNode("div", _hoisted_32, [
                                                        _renderSlot(_ctx.$slots, "empty", {}, () => [
                                                            _createElementVNode("p", _hoisted_33, _toDisplayString(_ctx.emptyStateText), 1 /* TEXT */)
                                                        ])
                                                    ])
                                                ], 2112 /* STABLE_FRAGMENT, DEV_ROOT_FRAGMENT */))
                                ])
                            ], 2112 /* STABLE_FRAGMENT, DEV_ROOT_FRAGMENT */))
                    ])
                ], 2 /* CLASS */)
            ], 2112 /* STABLE_FRAGMENT, DEV_ROOT_FRAGMENT */));
        };
    }
});
